import {
    lazy,
    Suspense,
    useEffect,
    useEffectEvent,
    useMemo,
    useRef,
    useState,
    type ReactNode
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    useRxCollection,
    useRxDatabase,
    useRxDocument
} from 'rxdb/plugins/react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import Dialog from '@mui/material/Dialog';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Skeleton from '@mui/material/Skeleton';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import DeleteIcon from '@mui/icons-material/Delete';
import AddLinkIcon from '@mui/icons-material/AddLink';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import Link from '@mui/material/Link';
import Alert from '@mui/material/Alert';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import GestureIcon from '@mui/icons-material/Gesture';
import EditIcon from '@mui/icons-material/Edit';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import Divider from '@mui/material/Divider';
import useMediaQuery from '@mui/material/useMediaQuery';
import { alpha } from '@mui/material/styles';
import HistoryIconButton from '../history/HistoryIconButton';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import { v4 as uuid } from 'uuid';
import {
    parseMediaGroupLayout,
    serializeMediaGroupLayout,
    type MediaDocType,
    type MediaGroupDocType,
    type MediaKind,
    type MediaLinkDocType,
    type MediaOwnerType,
    type ProjectDocType
} from '@ast-data/shared';
import {
    Link as RouterLink,
    useNavigate,
    useSearchParams
} from 'react-router-dom';
import { linkClick } from '../../components/routerLink';
import { useAbility, scopedSubject } from '../auth/abilityContext';
import {
    backgroundGoesWithSketch,
    coveredPhotoIds,
    planDetachFromOrigin,
    sceneBackgroundId,
    type MediaPlace
} from './mediaLinks';
import { PREVIEW_OWNER_TYPES } from './previewChoice';
import { usePreviewScope } from './usePreviewScope';
import { applyMediaRemoval } from './ownerMediaRemoval';
import type { AppDatabase } from '../../db/database';
import { useDocs, useOwnerMedia } from './useOwnerMedia';
import { groupSlots, partitionByGroups } from './mediaGroups';
import MediaLayoutDialog from './MediaLayoutDialog';
import {
    useMediaPlaceLabels,
    type MediaPlaceLabel
} from './useMediaPlaceLabels';
import MediaLinkDialog from './MediaLinkDialog';
import { compressImage } from './compressImage';
import type { PhotoPosition } from './jpegMetadata';
import { requestPhotoPosition, withPhotoPosition } from './photoPosition';
import { isPortableImage } from './imageFormat';
import { useFileDrop } from './useFileDrop';
import { useToast } from '../../components/toastContext';
import { copyToClipboard } from '../sync/syncReport';
import { MEDIA_FOCUS_PARAM, mediaDirectUrl } from './mediaDirectLink';
import { enqueueUpload } from './mediaUploader';
import { forgetMedia } from './blobStore';
import DownloadIcon from '@mui/icons-material/Download';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { useMediaBlobUrl, useMediaThumbUrl } from './useMediaBlobUrl';
import { loadMediaBlob, type MediaBlobProgress } from './loadMediaBlob';
import { downloadBlob } from '../../utils/downloadFile';
import {
    extensionFor,
    mediaFileName,
    mediaNamePrefix,
    type MediaLocation
} from './mediaFileName';
import { projectMeasureNumber } from '../projects/projectTitle';
import { scenePenIds, sceneStrata } from './sketch/scene';
import type { SketchStrata } from '../findings/sketchStrata';
/**
 * The drawing editor arrives when it is opened, not before: it brings Konva and
 * the whole tool set with it, a good quarter megabyte that everyone who merely
 * LOOKS at a documentation would otherwise load. `renderSceneToImage` is fetched
 * the same way. `scene.ts` stays static and is deliberately Konva-free.
 */
const SketchEditor = lazy(() => import('./SketchEditor'));
import ConfirmDialog from '../../components/ConfirmDialog';
import HistoryDialog from '../history/HistoryDialog';
import {
    hasGalleryOriginalsInApp,
    pickGalleryPhotosInApp,
    thisDevice,
    type DeviceIdentity
} from '../../lib/nativeApp';

function draftMedia(
    id: string,
    projectId: string,
    ownerType: MediaOwnerType,
    ownerId: string,
    kind: MediaKind,
    originalName: string,
    mimeType: string,
    byteSize: number,
    scene: string,
    caption: string,
    device: DeviceIdentity
): MediaDocType {
    const now = new Date().toISOString();
    return {
        id,
        projectId,
        ownerType,
        ownerId,
        kind,
        caption,
        originalName,
        mimeType,
        byteSize: String(byteSize),
        deviceName: device.name,
        deviceId: device.id,
        previewChosenAt: '',
        scene,
        createdBy: '',
        updatedBy: '',
        updatedAt: now,
        createdAt: now
    };
}

type EditorState =
    | { mode: 'new' }
    | { mode: 'annotate'; photoId: string }
    | { mode: 'edit'; mediaId: string; scene: string };

/** An editor title that says where else the sketch shows. */
function sharedTitle(
    title: string,
    places: MediaPlaceLabel[],
    intl: ReturnType<typeof useIntl>
): string {
    if (places.length === 0) {
        return title;
    }
    return intl.formatMessage(
        { id: 'media.link.editNotice' },
        {
            title,
            places: places.map((place) => place.label).join('; ')
        }
    );
}

/**
 * Media (photos + sketches) attached to any owner — a finding, Planum/Profil, or
 * a Gesamtbeschreibung — via (ownerType, ownerId): capture, thumbnail grid,
 * fullscreen viewer, sketch + photo-annotation editor. Owner-agnostic; the same
 * blob pipeline backs every owner.
 */
export default function MediaSection({
    projectId,
    ownerType,
    ownerId,
    canEdit,
    hideTitle = false,
    titleVariant = 'subtitle1',
    location,
    onSketchStrata,
    onSketchPens,
    strataTool = true,
    onNavigate
}: {
    projectId: string;
    ownerType: MediaOwnerType;
    ownerId: string;
    canEdit: boolean;
    /** Drop the heading where the surrounding UI already names the section. */
    hideTitle?: boolean;
    /** Heading variant — 'h6' matches the office dialogs' section boxes. */
    titleVariant?: 'subtitle1' | 'h6';
    /**
     * Where these pictures were taken (Befund/Fläche/Planum or Profil), which
     * every download is named after. Comes from the owning form rather than from
     * the stored record so a number just typed is already in the file name.
     */
    location?: MediaLocation;
    /**
     * The Schichten drawn on these sketches, for the Planum/Profil forms that
     * seed their list from them — a layer drawn on a sketch is already named,
     * and the form only wants its measures. Absent for every other owner.
     */
    onSketchStrata?: (sketches: SketchStrata[]) => void;
    /**
     * The pens these sketches are drawn with (PEN_PRESETS ids), for the forms
     * that seed their Einschlüsse from them. Absent for every other owner.
     */
    onSketchPens?: (penIds: string[]) => void;
    /**
     * Whether the sketch editor offers its Schichten tool. Off for a „baulicher
     * Bestand", which is measured in Lagen and has no Schichten list to seed.
     * Marks already on a saved sketch keep rendering either way.
     */
    strataTool?: boolean;
    /**
     * How a click on "auch in Bef. …" leaves: the owning dialog's guarded
     * navigate, so unsaved changes are asked about first. Plain navigation
     * where omitted.
     */
    onNavigate?: (to: string) => void;
}) {
    const intl = useIntl();
    const { notify } = useToast();
    const navigate = useNavigate();
    const ability = useAbility();
    const collection = useRxCollection<MediaDocType>('media');
    const database = useRxDatabase() as unknown as AppDatabase | null;
    const [linkDialogOpen, setLinkDialogOpen] = useState(false);
    // Two inputs for one pipeline: the camera one carries `capture`, which makes
    // a phone skip its picker and shoot straight away — and which is exactly why
    // it can't be the only way in. See the inputs below.
    const cameraInput = useRef<HTMLInputElement>(null);
    const galleryInput = useRef<HTMLInputElement>(null);
    /** The position asked for when the camera opened (see photoPosition). */
    const cameraFix = useRef<Promise<PhotoPosition | null> | null>(null);
    const [editor, setEditor] = useState<EditorState | null>(null);
    const [layoutOpen, setLayoutOpen] = useState(false);
    /**
     * Pictures picked but not stored yet — by the id their record WILL have.
     * Shrinking a camera photo takes seconds on a tablet, and only then does
     * its record exist; until it does, a placeholder card says the picture has
     * arrived.
     */
    const [preparing, setPreparing] = useState<string[]>([]);

    // Its own pictures and those linked in from another description.
    const { shown, placesByMedia, links } = useOwnerMedia(ownerType, ownerId);
    const media = useMemo(() => shown.map((item) => item.media), [shown]);
    const linkByMediaId = useMemo(
        () => new Map(shown.map((item) => [item.media.id, item.link])),
        [shown]
    );
    const mediaById = useMemo(
        () => new Map(media.map((item) => [item.id, item])),
        [media]
    );
    const shownIds = useMemo(
        () => new Set(media.map((item) => item.id)),
        [media]
    );
    const placeLabels = useMediaPlaceLabels(projectId);
    // The picture a direct link points at (see MediaRedirect).
    const [searchParams] = useSearchParams();
    const focusedMediaId = searchParams.get(MEDIA_FOCUS_PARAM);
    const waiting = preparing.filter((key) => !shownIds.has(key));

    /** Where else a picture shows, as labelled links; unknown places left out. */
    function otherPlaces(mediaId: string): MediaPlaceLabel[] {
        return (placesByMedia.get(mediaId) ?? [])
            .filter((place: MediaPlace) => place.ownerId !== ownerId)
            .flatMap((place) => {
                const label = placeLabels.get(place.ownerId);
                return label ? [label] : [];
            });
    }

    /**
     * Taking an ORIGINAL out of this description while it stays elsewhere: the
     * oldest place it is linked into becomes its new origin. Absent where no
     * such place exists, or where the picture is not ours to move.
     */
    function detachFor(
        item: MediaDocType
    ): { heirLabel: string; detach: () => Promise<void> } | undefined {
        if (linkByMediaId.get(item.id) || !mayEditMedia(item)) {
            return undefined;
        }
        const background =
            item.kind === 'sketch' ? sceneBackgroundId(item.scene) : '';
        // Only a place that still exists AND whose project this user may
        // write: the picture moves into it, and that link goes.
        const heirs = new Set(
            links
                .filter(
                    (link) =>
                        link.mediaId === item.id &&
                        placeLabels.has(link.ownerId) &&
                        ability.can(
                            'update',
                            scopedSubject('Media', {
                                projectId: link.projectId
                            })
                        ) &&
                        ability.can(
                            'delete',
                            scopedSubject('MediaLink', {
                                projectId: link.projectId
                            })
                        )
                )
                .map((link) => link.ownerId)
        );
        const plan = planDetachFromOrigin(
            item,
            background ? mediaById.get(background) : undefined,
            links,
            heirs
        );
        const heir = plan?.moves[0];
        if (!plan || !heir || !database) {
            return undefined;
        }
        return {
            heirLabel: placeLabels.get(heir.ownerId)?.label ?? '',
            detach: () => applyMediaRemoval(database, plan)
        };
    }

    // A linked picture may come from a sibling phase, whose project decides
    // whether it may be changed.
    function mayEditMedia(item: MediaDocType): boolean {
        return (
            canEdit &&
            ability.can(
                'update',
                scopedSubject('Media', { projectId: item.projectId })
            )
        );
    }

    /**
     * Deleting is a right of its own: a Teamleitung (Junior) adds and edits
     * pictures but leaves throwing them away to the office. Offered without
     * it, the server refuses and the device rolls the deletion back. Covers
     * everything doDelete removes — the photo under a sketch and every link.
     */
    function mayDeleteMedia(item: MediaDocType): boolean {
        const background =
            item.kind === 'sketch'
                ? mediaById.get(sceneBackgroundId(item.scene))
                : undefined;
        // The same rule doDelete follows: the photo goes only where it is
        // the sketch's alone.
        const going =
            background && backgroundGoesWithSketch(item, background, links)
                ? [item, background]
                : [item];
        return (
            canEdit &&
            going.every((one) =>
                ability.can(
                    'delete',
                    scopedSubject('Media', { projectId: one.projectId })
                )
            ) &&
            links
                .filter((link) => going.some((one) => one.id === link.mediaId))
                .every((link) =>
                    ability.can(
                        'delete',
                        scopedSubject('MediaLink', {
                            projectId: link.projectId
                        })
                    )
                )
        );
    }

    /**
     * Unlinking removes the links here — the picture's and, for a sketch, its
     * photo's, as doUnlink does — each decided by its own project.
     */
    function mayUnlink(item: MediaDocType): boolean {
        const link = linkByMediaId.get(item.id);
        if (!canEdit || !link) {
            return false;
        }
        const background =
            item.kind === 'sketch' ? sceneBackgroundId(item.scene) : '';
        return links
            .filter(
                (one) =>
                    one.ownerId === link.ownerId &&
                    (one.mediaId === item.id ||
                        (!!background && one.mediaId === background))
            )
            .every((one) =>
                ability.can(
                    'delete',
                    scopedSubject('MediaLink', { projectId: one.projectId })
                )
            );
    }

    // A download leads with the Maßnahmennummer, which only the project knows.
    const projectsCollection = useRxCollection<ProjectDocType>('projects');
    const { result: projectDoc } = useRxDocument<ProjectDocType>(
        projectsCollection,
        projectId
    );
    const projects = useMemo(
        () =>
            projectDoc ? (projectDoc.toMutableJSON() as ProjectDocType) : null,
        [projectDoc]
    );
    // Keyed on the Lage's parts, not on the object: the owning form passes a
    // fresh literal on every keystroke.
    const namePrefix = useMemo(
        () => mediaNamePrefix(projectMeasureNumber(projects), location ?? {}),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            projects,
            location?.findingNumber,
            location?.overallDescriptionNumber,
            location?.area,
            location?.planumNumber,
            location?.profileNumber,
            location?.diaryDate
        ]
    );

    // A photo that has been annotated lives on as a LAYER of its sketch (the
    // scene records it as `bg`, and the editor can toggle it visible) — so the
    // gallery shows only the sketch, not the same image twice. Deleting the
    // sketch takes the photo with it (see MediaCard.doDelete).
    const visibleMedia = useMemo(() => {
        const annotatedPhotoIds = coveredPhotoIds(media);
        return media.filter(
            (item) => !(item.kind === 'photo' && annotatedPhotoIds.has(item.id))
        );
    }, [media]);

    // The picture the field list card shows — for a Befund, a Planum or a
    // Profil the one starred for the whole Befund (see usePreviewScope).
    const { starred, previewId } = usePreviewScope(
        projectId,
        ownerType,
        ownerId
    );
    const offersPreview = PREVIEW_OWNER_TYPES.includes(ownerType);

    /**
     * Star a picture as the preview, or take the star back. Either way every
     * other star of the scope goes: a star taken back must not bring an older
     * choice back that nobody remembers making.
     */
    async function togglePreview(item: MediaDocType) {
        if (!collection) {
            return;
        }
        const now = new Date().toISOString();
        const choosing = item.id !== previewId;
        const cleared = starred.filter(
            (picture) => picture.id !== item.id && mayEditMedia(picture)
        );
        const changes = [
            ...cleared.map((picture) => ({ id: picture.id, chosenAt: '' })),
            { id: item.id, chosenAt: choosing ? now : '' }
        ];
        for (const { id, chosenAt } of changes) {
            const live = await collection.findOne(id).exec();
            await live?.incrementalPatch({
                previewChosenAt: chosenAt,
                updatedAt: now
            });
        }
    }

    // Pictures printed together in a grid: a group only holds what this
    // description still shows, so a deleted or unlinked picture drops out.
    const groups = useDocs<MediaGroupDocType>('media_groups', {
        ownerType,
        ownerId
    });
    const { singles, groups: resolvedGroups } = useMemo(
        () =>
            partitionByGroups(
                visibleMedia,
                (item) => item.id,
                groupSlots(groups, new Set(visibleMedia.map((item) => item.id)))
            ),
        [visibleMedia, groups]
    );
    const canGroup =
        canEdit &&
        ability.can('create', scopedSubject('MediaGroup', { projectId }));
    // The plates are arranged in their own dialog; the gallery only leads there.
    // Also while any plate stands, however few pictures are left: a plate
    // down to one picture still prints, and only the dialog can take it apart.
    const showLayoutButton =
        canGroup && (visibleMedia.length >= 2 || resolvedGroups.length > 0);

    /**
     * The Schichten drawn on each sketch. A sketch is named the way the Befund
     * sheet names it — its caption, else "Skizze n" by position — so a seeded row
     * and its drawing carry the same name on screen and on paper.
     *
     * Reported through an effect keyed on the VALUE, not on the media array:
     * every RxDB emission is a fresh array, and the form would take an identical
     * list as a change and mark itself dirty.
     */
    const sketchStrata = useMemo<SketchStrata[]>(() => {
        // Numbered in the sheet's order: single sketches, then the grids.
        return [...singles, ...resolvedGroups.flatMap((entry) => entry.members)]
            .filter((item) => item.kind === 'sketch')
            .map((item, index) => {
                return {
                    sketchLabel:
                        item.caption.trim() ||
                        `${intl.formatMessage({ id: 'media.sketch' })} ${index + 1}`,
                    strata: sceneStrata(item.scene)
                };
            })
            .filter((entry) => entry.strata.length > 0);
    }, [singles, resolvedGroups, intl]);
    const sketchStrataKey = JSON.stringify(sketchStrata);
    const reportStrata = useEffectEvent((strata: SketchStrata[]) => {
        onSketchStrata?.(strata);
    });
    useEffect(() => {
        reportStrata(JSON.parse(sketchStrataKey) as SketchStrata[]);
    }, [sketchStrataKey]);

    // The pens of every sketch together — reported by value, like the Schichten.
    const sketchPensKey = useMemo(
        () =>
            JSON.stringify(
                Array.from(
                    new Set(
                        media
                            .filter((item) => item.kind === 'sketch')
                            .flatMap((item) => scenePenIds(item.scene))
                    )
                ).sort()
            ),
        [media]
    );
    const reportPens = useEffectEvent((penIds: string[]) => {
        onSketchPens?.(penIds);
    });
    // Also once the form starts listening: it only does when the user may
    // write and its Befund has loaded, which can come after the media.
    const pensWanted = onSketchPens !== undefined;
    useEffect(() => {
        if (pensWanted) {
            reportPens(JSON.parse(sketchPensKey) as string[]);
        }
    }, [sketchPensKey, pensWanted]);

    async function storeBlob(
        kind: MediaKind,
        originalName: string,
        blob: Blob,
        mimeType: string,
        scene: string,
        caption = '',
        id = uuid()
    ): Promise<string | null> {
        if (!collection) {
            return null;
        }
        // A photo records the device that took it; a sketch is drawn anywhere.
        const device =
            kind === 'photo' ? await thisDevice() : { name: '', id: '' };
        await collection.insert(
            draftMedia(
                id,
                projectId,
                ownerType,
                ownerId,
                kind,
                originalName,
                mimeType,
                blob.size,
                scene,
                caption,
                device
            )
        );
        await enqueueUpload(id, blob, mimeType);
        return id;
    }

    // Take whatever the picker handed over — a fresh shot, a picked file or
    // files dropped onto the section. A picker's input is cleared afterwards so
    // the SAME file picked twice still fires a change event; `input` rather
    // than a ref, because two inputs feed here.
    async function pickInApp() {
        try {
            await onFilesSelected(await pickGalleryPhotosInApp());
        } catch (cause) {
            console.error('Picking photos in the app failed', cause);
            notify(intl.formatMessage({ id: 'media.galleryFailed' }), 'error');
        }
    }

    async function onFilesSelected(
        files: FileList | File[] | null,
        input?: HTMLInputElement,
        cameraPosition?: Promise<PhotoPosition | null> | null
    ) {
        if (!files || !collection || !canEdit) {
            return;
        }
        const picked = Array.from(files).map((file) => ({
            key: uuid(),
            file
        }));
        setPreparing((current) => [
            ...current,
            ...picked.map((entry) => entry.key)
        ]);
        if (input) {
            input.value = '';
        }
        const rejected: string[] = [];
        const failed: string[] = [];
        for (const { key, file } of picked) {
            try {
                const { blob, mimeType } = await compressImage(
                    file,
                    undefined,
                    undefined,
                    { keepTurned: true }
                );
                /**
                 * Every picture the browser can read comes out as JPEG or PNG.
                 * Anything else — an iPhone HEIC on a Windows PC, an SVG, a
                 * TIFF, a PDF — it could not: stored, it would show nowhere,
                 * have no preview and be missing from the PDF.
                 */
                if (!isPortableImage(mimeType)) {
                    rejected.push(file.name);
                    continue;
                }
                const located = cameraPosition
                    ? await withPhotoPosition(blob, cameraPosition)
                    : blob;
                await storeBlob(
                    'photo',
                    file.name,
                    located,
                    mimeType,
                    '',
                    '',
                    key
                );
            } catch (cause) {
                // One file that cannot be read or stored must not take the
                // rest of the batch down with it.
                failed.push(file.name);
                console.error('Storing a picked picture failed', cause);
            } finally {
                setPreparing((current) =>
                    current.filter((entry) => entry !== key)
                );
            }
        }
        if (rejected.length > 0) {
            notify(
                intl.formatMessage(
                    { id: 'media.unsupportedFormat' },
                    { names: rejected.join(', ') }
                ),
                'warning'
            );
        }
        if (failed.length > 0) {
            notify(
                intl.formatMessage(
                    { id: 'media.addFailed' },
                    { names: failed.join(', ') }
                ),
                'error'
            );
        }
    }

    /**
     * A new sketch — blank, or drawn on `photo`. The photo then disappears
     * behind its sketch in the gallery, so the sketch takes its place: its
     * name, and its cell on any plate of this description.
     */
    async function createSketch(
        blob: Blob,
        scene: string,
        photo?: MediaDocType
    ) {
        // The editor picks the format by content (PNG for line work, JPEG once
        // a photo is baked in), so the type travels WITH the blob rather than
        // being asserted here.
        const mimeType = blob.type || 'image/png';
        const sketchId = await storeBlob(
            'sketch',
            `sketch${extensionFor(mimeType) || '.png'}`,
            blob,
            mimeType,
            scene,
            photo?.caption ?? ''
        );
        if (sketchId && photo) {
            await takePlateCell(photo.id, sketchId);
        }
        setEditor(null);
    }

    /** Puts `sketchId` wherever `photoId` stands on this description's plates. */
    async function takePlateCell(photoId: string, sketchId: string) {
        const now = new Date().toISOString();
        for (const group of groups) {
            const layout = parseMediaGroupLayout(group.layout);
            if (!layout.some((row) => row.includes(photoId))) {
                continue;
            }
            const next = layout.map((row) =>
                row.map((id) => (id === photoId ? sketchId : id))
            );
            await (
                await database?.media_groups.findOne(group.id).exec()
            )?.incrementalPatch({
                layout: serializeMediaGroupLayout(next),
                updatedAt: now
            });
        }
    }

    // The card's edit action: annotating a photo, editing a sketch. Absent for
    // read-only users and for sketches without a re-editable scene.
    function editorFor(item: MediaDocType): (() => void) | undefined {
        if (!canEdit) {
            return undefined;
        }
        if (item.kind === 'sketch' && item.scene) {
            if (!mayEditMedia(item)) {
                return undefined;
            }
            return () =>
                setEditor({
                    mode: 'edit',
                    mediaId: item.id,
                    scene: item.scene
                });
        }
        if (item.kind === 'photo') {
            return () => setEditor({ mode: 'annotate', photoId: item.id });
        }
        return undefined;
    }

    async function updateSketch(mediaId: string, blob: Blob, scene: string) {
        // The type comes from the blob: the editor now picks PNG for line work,
        // and asserting JPEG here would have labelled those bytes wrongly.
        const mimeType = blob.type || 'image/png';
        await replaceSketchImage(mediaId, blob, mimeType, scene);
        setEditor(null);
    }

    /**
     * Overwrite a sketch's stored image, optionally along with its scene.
     *
     * Shared by saving the editor and by re-rendering an old sketch, so both
     * leave the record in the same state — same patched fields, same upload.
     */
    async function replaceSketchImage(
        mediaId: string,
        blob: Blob,
        mimeType: string,
        scene?: string
    ) {
        // The new bytes are on this device BEFORE the doc changes: the change
        // re-renders the card, which must find them pending rather than ask
        // the server — that still holds the old image.
        await enqueueUpload(mediaId, blob, mimeType);
        if (collection) {
            const live = await collection.findOne(mediaId).exec();
            await live?.incrementalPatch({
                ...(scene === undefined ? {} : { scene }),
                mimeType,
                byteSize: String(blob.size),
                updatedAt: new Date().toISOString()
            });
        }
    }

    // Draw an existing sketch again from its scene and replace the stored image —
    // for sketches flattened before the export reached full resolution, whose
    // marks are intact and only whose baked pixels are stale. That cached image
    // is what the Befund PDF and the ExcaBook bundle embed.
    async function rebakeSketch(item: MediaDocType): Promise<boolean> {
        if (!item.scene) {
            return false;
        }
        const { renderSceneToImage } = await import('./sketch/renderScene');
        const rendered = await renderSceneToImage(item.scene);
        if (!rendered) {
            return false;
        }
        await replaceSketchImage(item.id, rendered.blob, rendered.mimeType);
        return true;
    }

    const { active: dropActive, dropProps } = useFileDrop(canEdit, (files) => {
        void onFilesSelected(files);
    });
    // Only a mouse drags files in; a tablet would be told about a gesture it
    // cannot make.
    const canDragFiles = useMediaQuery('(pointer: fine)');

    function renderCard(item: MediaDocType) {
        return (
            // Key includes updatedAt so an edited sketch's preview remounts
            // and re-reads the refreshed local cache.
            <MediaCard
                key={`${item.id}:${item.updatedAt}`}
                media={item}
                focused={item.id === focusedMediaId}
                namePrefix={namePrefix}
                canEdit={canEdit}
                canEditMedia={mayEditMedia(item)}
                canDelete={mayDeleteMedia(item)}
                canUnlink={mayUnlink(item)}
                linkHere={linkByMediaId.get(item.id) ?? null}
                mediaById={mediaById}
                links={links}
                otherPlaces={otherPlaces(item.id)}
                onOpenPlace={(to) =>
                    onNavigate ? onNavigate(to) : navigate(to)
                }
                detach={detachFor(item)}
                previewChosen={offersPreview && item.id === previewId}
                onTogglePreview={
                    offersPreview && mayEditMedia(item)
                        ? () => togglePreview(item)
                        : undefined
                }
                onEdit={editorFor(item)}
                onRebake={
                    SHOW_REBAKE_ACTION &&
                    canEdit &&
                    item.kind === 'sketch' &&
                    item.scene
                        ? () => rebakeSketch(item)
                        : undefined
                }
            />
        );
    }

    return (
        <Box {...dropProps} sx={{ position: 'relative' }}>
            {dropActive && (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: 2,
                        borderStyle: 'dashed',
                        borderColor: 'primary.main',
                        borderRadius: 1,
                        bgcolor: (theme) =>
                            alpha(theme.palette.background.paper, 0.9),
                        // The counter in useFileDrop sees the children, not this.
                        pointerEvents: 'none'
                    }}
                >
                    <Typography sx={{ color: 'primary.main' }}>
                        <FormattedMessage id="media.dropHere" />
                    </Typography>
                </Box>
            )}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: hideTitle ? 'flex-end' : 'space-between',
                    mb: 1
                }}
            >
                {!hideTitle && (
                    <Typography variant={titleVariant}>
                        <FormattedMessage id="media.title" />
                    </Typography>
                )}
                {canEdit && (
                    <Stack
                        direction="row"
                        spacing={1}
                        useFlexGap
                        sx={{ flexWrap: 'wrap', justifyContent: 'flex-end' }}
                    >
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AddAPhotoIcon />}
                            onClick={() => {
                                // Only where `capture` really opens a camera: a
                                // desktop shows a file dialog instead, and a
                                // file from disk was not taken here.
                                cameraFix.current = window.matchMedia(
                                    '(pointer: coarse)'
                                ).matches
                                    ? requestPhotoPosition()
                                    : null;
                                cameraInput.current?.click();
                            }}
                        >
                            <FormattedMessage id="media.add" />
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<PhotoLibraryIcon />}
                            onClick={() => {
                                // The app's picker keeps the position the
                                // browser's file input loses.
                                if (hasGalleryOriginalsInApp()) {
                                    void pickInApp();
                                } else {
                                    galleryInput.current?.click();
                                }
                            }}
                        >
                            <FormattedMessage id="media.addFromGallery" />
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<GestureIcon />}
                            onClick={() => setEditor({ mode: 'new' })}
                        >
                            <FormattedMessage id="media.sketch" />
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AddLinkIcon />}
                            onClick={() => setLinkDialogOpen(true)}
                        >
                            <FormattedMessage id="media.link.add" />
                        </Button>
                    </Stack>
                )}
            </Box>
            {/* `capture` is a REQUEST for the camera, and a phone grants it by
                skipping its own picker entirely — one tap to a picture in the
                trench, but no way to a photo already on the device. So the two
                routes are two inputs; the desktop ignores `capture` and shows
                the same file dialog for both. */}
            <input
                ref={cameraInput}
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                hidden
                onChange={(event) => {
                    const fix = cameraFix.current;
                    cameraFix.current = null;
                    void onFilesSelected(event.target.files, event.target, fix);
                }}
            />
            <input
                ref={galleryInput}
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={(event) =>
                    void onFilesSelected(event.target.files, event.target)
                }
            />
            {visibleMedia.length === 0 && waiting.length === 0 ? (
                <Typography
                    variant="body2"
                    sx={{
                        color: 'text.secondary'
                    }}
                >
                    <FormattedMessage
                        id={
                            canEdit && canDragFiles
                                ? 'media.emptyDrop'
                                : 'media.empty'
                        }
                    />
                </Typography>
            ) : (
                <Stack spacing={2}>
                    {/* One card per row on a phone, two from sm, three from
                        md — the cards fill their column. */}
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: '1fr',
                                sm: 'repeat(2, minmax(0, 1fr))',
                                md: 'repeat(3, minmax(0, 1fr))'
                            },
                            gap: 1.5
                        }}
                    >
                        {visibleMedia.map(renderCard)}
                        {/* Gone in the same render its picture's card arrives
                            in: the record shows up before storing it is done,
                            and both side by side for a moment pushed the
                            gallery a row down and back. */}
                        {waiting.map((key) => (
                            <PreparingCard key={key} />
                        ))}
                    </Box>
                    {/* How the PDF groups them lives behind this, not here. */}
                    {showLayoutButton && (
                        <>
                            <Divider />
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-end'
                                }}
                            >
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<ViewModuleIcon />}
                                    onClick={() => setLayoutOpen(true)}
                                >
                                    <FormattedMessage id="media.group.add" />
                                </Button>
                            </Box>
                        </>
                    )}
                </Stack>
            )}
            {layoutOpen && (
                <MediaLayoutDialog
                    onClose={() => setLayoutOpen(false)}
                    projectId={projectId}
                    ownerType={ownerType}
                    ownerId={ownerId}
                    placeLabel={placeLabels.get(ownerId)?.label}
                    namePrefix={namePrefix}
                    pictures={visibleMedia}
                    groups={resolvedGroups}
                />
            )}
            {editor?.mode === 'new' && (
                <SketchHost
                    title={intl.formatMessage({ id: 'sketch.title' })}
                    onClose={() => setEditor(null)}
                    onSaveMedia={createSketch}
                    strataTool={strataTool}
                />
            )}
            {editor?.mode === 'annotate' && (
                <SketchHost
                    title={intl.formatMessage({ id: 'sketch.annotate.title' })}
                    sourcePhotoId={editor.photoId}
                    onClose={() => setEditor(null)}
                    onSaveMedia={(blob, scene) =>
                        createSketch(blob, scene, mediaById.get(editor.photoId))
                    }
                    strataTool={strataTool}
                />
            )}
            {/* Mounted only while open: it queries the whole family. */}
            {canEdit && linkDialogOpen && (
                <MediaLinkDialog
                    open
                    onClose={() => setLinkDialogOpen(false)}
                    projectId={projectId}
                    ownerType={ownerType}
                    ownerId={ownerId}
                    shownIds={shownIds}
                />
            )}
            {editor?.mode === 'edit' && (
                <SketchHost
                    title={sharedTitle(
                        intl.formatMessage({ id: 'sketch.title' }),
                        otherPlaces(editor.mediaId),
                        intl
                    )}
                    scene={editor.scene}
                    onClose={() => setEditor(null)}
                    onSaveMedia={(blob, scene) =>
                        updateSketch(editor.mediaId, blob, scene)
                    }
                    strataTool={strataTool}
                />
            )}
        </Box>
    );
}

/**
 * Mounts the sketch editor for one of three cases — a blank sketch, annotating a
 * photo, or re-editing a saved sketch — resolving the background photo's blob URL
 * first when one is needed (fresh annotation's source photo, or the photo a saved
 * scene was drawn over) so it can be shown behind the strokes and re-flattened.
 */
function SketchHost({
    title,
    scene,
    sourcePhotoId,
    onClose,
    onSaveMedia,
    strataTool
}: {
    title: string;
    scene?: string;
    sourcePhotoId?: string;
    onClose: () => void;
    onSaveMedia: (blob: Blob, scene: string) => void | Promise<void>;
    strataTool: boolean;
}) {
    const backgroundId =
        sourcePhotoId ?? (scene ? sceneBackgroundId(scene) : '');

    if (backgroundId) {
        return (
            <SketchWithBackground
                title={title}
                scene={scene}
                backgroundId={backgroundId}
                onClose={onClose}
                onSaveMedia={onSaveMedia}
                strataTool={strataTool}
            />
        );
    }

    return (
        <Suspense fallback={<SketchLoading onClose={onClose} />}>
            <SketchEditor
                title={title}
                scene={scene}
                onCancel={onClose}
                onSave={onSaveMedia}
                strataTool={strataTool}
            />
        </Suspense>
    );
}

/**
 * Fullscreen stand-in for the editor while something it needs is on its way —
 * its own code, or the photo it draws over. Both waits look the same to whoever
 * is waiting, so they say the same thing: a spinner and a way out.
 */
function SketchLoading({
    onClose,
    notice,
    progress
}: {
    onClose: () => void;
    /**
     * The photo coming down from the server. A bare spinner over half a
     * minute of mobile download reads as a hang; megabytes counting up do not.
     */
    progress?: MediaBlobProgress | null;
    /**
     * Shown INSTEAD of the spinner when the wait ended without a result — the
     * background photo isn't on this device. The way out stays either way.
     */
    notice?: ReactNode;
}) {
    return (
        <Dialog fullScreen open onClose={onClose}>
            <Stack
                spacing={2}
                sx={{
                    height: '100%',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                {notice ??
                    (progress ? (
                        <DownloadProgress progress={progress} />
                    ) : (
                        <CircularProgress />
                    ))}
                <Button variant="outlined" onClick={onClose}>
                    <FormattedMessage id="common.cancel" />
                </Button>
            </Stack>
        </Dialog>
    );
}

/** Megabytes, with one decimal — what a photo's size is talked about in. */
function megabytes(bytes: number): number {
    return Math.round((bytes / (1024 * 1024)) * 10) / 10;
}

function DownloadProgress({ progress }: { progress: MediaBlobProgress }) {
    const { loaded, total } = progress;
    return (
        <Box sx={{ width: 'min(320px, 80vw)', textAlign: 'center' }}>
            <LinearProgress
                variant={total ? 'determinate' : 'indeterminate'}
                value={
                    total ? Math.min(100, (loaded / total) * 100) : undefined
                }
            />
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                {total ? (
                    <FormattedMessage
                        id="media.downloadProgress"
                        values={{
                            loaded: megabytes(loaded),
                            total: megabytes(total)
                        }}
                    />
                ) : (
                    <FormattedMessage
                        id="media.downloadLoaded"
                        values={{ loaded: megabytes(loaded) }}
                    />
                )}
            </Typography>
        </Box>
    );
}

function SketchWithBackground({
    title,
    scene,
    backgroundId,
    onClose,
    onSaveMedia,
    strataTool
}: {
    title: string;
    scene?: string;
    backgroundId: string;
    onClose: () => void;
    onSaveMedia: (blob: Blob, scene: string) => void | Promise<void>;
    strataTool: boolean;
}) {
    const { url, loading, progress } = useMediaBlobUrl(backgroundId);

    if (!url) {
        return (
            <SketchLoading
                onClose={onClose}
                progress={progress}
                notice={
                    loading ? undefined : (
                        <Typography sx={{ color: 'text.secondary' }}>
                            <FormattedMessage id="media.unavailable" />
                        </Typography>
                    )
                }
            />
        );
    }

    return (
        <Suspense fallback={<SketchLoading onClose={onClose} />}>
            <SketchEditor
                title={title}
                scene={scene}
                backgroundUrl={url}
                backgroundMediaId={backgroundId}
                onCancel={onClose}
                onSave={onSaveMedia}
                strataTool={strataTool}
            />
        </Suspense>
    );
}

/**
 * Whether a sketch card offers "re-render from the drawing", REPLACING the
 * stored image. Off for now: downloading already re-renders, so what people see
 * is fixed without touching stored data. Turn it on when the copy the PDF and
 * the ExcaBook bundle embed needs refreshing — only the button is withheld.
 */
const SHOW_REBAKE_ACTION = false;

const CARD_MEDIA_HEIGHT = 180;

/** Where a picture's card will be, while the picture is still being prepared. */
function PreparingCard() {
    return (
        <Card variant="outlined" sx={{ minWidth: 0 }} aria-busy>
            <Skeleton
                variant="rectangular"
                animation="wave"
                height={CARD_MEDIA_HEIGHT}
            />
            <CardContent sx={{ p: 1.25, pb: 1, '&:last-child': { pb: 1 } }}>
                <Skeleton variant="rounded" height={40} />
            </CardContent>
            {/* The action row's height, so the real card takes its place
                without the rows below jumping. */}
            <CardActions sx={{ pt: 0 }}>
                <Skeleton variant="rounded" width={96} height={38} />
            </CardActions>
        </Card>
    );
}

/**
 * One photo/sketch as a card: a large preview (tap = straight into the
 * editor), a name field defaulting to the uploaded file's name, and the
 * edit/history/delete actions. Replaces the old fullscreen viewer.
 */
function MediaCard({
    media,
    focused,
    namePrefix,
    canEdit,
    canEditMedia,
    canDelete,
    canUnlink,
    linkHere,
    mediaById,
    links,
    otherPlaces,
    onOpenPlace,
    detach,
    previewChosen,
    onTogglePreview,
    onEdit,
    onRebake
}: {
    media: MediaDocType;
    /** The picture a direct link points at: scrolled to and marked. */
    focused: boolean;
    /** Maßnahme + Lage, which every download of this card is named after. */
    namePrefix: string;
    /** May change what THIS description shows (add, unlink). */
    canEdit: boolean;
    /** May change the picture itself — decided by the picture's own project. */
    canEditMedia: boolean;
    /** May delete the picture — with its photo and links, see mayDeleteMedia. */
    canDelete: boolean;
    /** May take the picture out of THIS description. */
    canUnlink: boolean;
    /** The link that shows this picture here; null where it is its origin. */
    linkHere: MediaLinkDocType | null;
    /** Every picture of this gallery, the covered photos included. */
    mediaById: ReadonlyMap<string, MediaDocType>;
    /** Every link of those pictures, wherever it points. */
    links: readonly MediaLinkDocType[];
    /** The other descriptions this picture shows in. */
    otherPlaces: MediaPlaceLabel[];
    onOpenPlace: (to: string) => void;
    /**
     * Take this ORIGINAL out of here only, handing it to `heirLabel`. Absent
     * on a linked card, and where nothing else shows the picture.
     */
    detach?: { heirLabel: string; detach: () => Promise<void> };
    /** The picture is this description's preview on the field lists. */
    previewChosen: boolean;
    /** Stars the picture as the preview, or takes the star back. */
    onTogglePreview?: () => Promise<void>;
    /** Opens the annotation/sketch editor; absent when there is nothing to edit. */
    onEdit?: () => void;
    /**
     * Re-render this sketch from its scene and replace the stored image.
     * Absent for photos, for scene-less sketches and for read-only users.
     */
    onRebake?: () => Promise<boolean>;
}) {
    const intl = useIntl();
    const { notify } = useToast();
    const collection = useRxCollection<MediaDocType>('media');
    const linkCollection = useRxCollection<MediaLinkDocType>('media_links');
    // The card shows a small preview; downloading fetches the original.
    const { url, loading, pending } = useMediaThumbUrl(
        media.id,
        media.updatedAt
    );
    const [caption, setCaption] = useState(media.caption);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [confirmDetach, setConfirmDetach] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [starring, setStarring] = useState(false);
    const cardRef = useRef<HTMLDivElement | null>(null);

    // Opened from a direct link: bring this card into view. Runs after the
    // dialog's own jump to its media section (FormSection), so this one wins.
    useEffect(() => {
        if (!focused) {
            return;
        }
        const frame = requestAnimationFrame(() =>
            cardRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            })
        );
        return () => cancelAnimationFrame(frame);
    }, [focused]);

    async function togglePreview() {
        if (!onTogglePreview || starring) {
            return;
        }
        setStarring(true);
        try {
            await onTogglePreview();
        } catch {
            notify(intl.formatMessage({ id: 'media.preview.failed' }), 'error');
        } finally {
            setStarring(false);
        }
    }

    async function copyDirectLink() {
        const url = mediaDirectUrl(media.id);
        const copied = await copyToClipboard(url);
        // Where the clipboard is out of reach, the link itself is the
        // message, to be copied by hand.
        notify(
            intl.formatMessage(
                {
                    id: copied
                        ? 'media.directLink.copied'
                        : 'media.directLink.copyFailed'
                },
                { url }
            ),
            copied ? 'success' : 'error'
        );
    }

    /** The photo this sketch was drawn over, deleted along with it. */
    const backgroundPhotoId =
        media.kind === 'sketch' && media.scene
            ? sceneBackgroundId(media.scene)
            : '';

    // Save this image to disk. A SKETCH IS DRAWN AGAIN FROM ITS SCENE rather
    // than copied out of the store: the scene is the original, the stored image
    // only a cache of it. Falls back to the stored bytes when there is no scene,
    // or when its background photo is not on this device.
    async function download() {
        if (media.kind === 'sketch' && media.scene) {
            setBusy(true);
            try {
                const { renderSceneToImage } =
                    await import('./sketch/renderScene');
                const rendered = await renderSceneToImage(media.scene);
                if (rendered) {
                    downloadBlob(
                        rendered.blob,
                        mediaFileName(media, namePrefix, rendered.mimeType)
                    );
                    return;
                }
            } finally {
                setBusy(false);
            }
        }
        setBusy(true);
        try {
            const original = await loadMediaBlob(media.id);
            if (original) {
                downloadBlob(original, mediaFileName(media, namePrefix));
            }
        } finally {
            setBusy(false);
        }
    }

    async function rebake() {
        if (!onRebake) {
            return;
        }
        setBusy(true);
        try {
            await onRebake();
        } finally {
            setBusy(false);
        }
    }

    async function saveCaption() {
        if (!collection || caption === media.caption) {
            return;
        }
        const live = await collection.findOne(media.id).exec();
        await live?.incrementalPatch({
            caption,
            updatedAt: new Date().toISOString()
        });
    }

    /**
     * The caption writes itself on blur — but the field can lose the PAGE rather
     * than the focus, and an unmount fires no blur. That dropped captions typed
     * in a record dialog: Enter submits the surrounding form and the card goes
     * with it. So the last word is written on the way out, FROM REFS, since the
     * unmount effect would otherwise close over the first render's value.
     *
     * Only a write when the text actually CHANGED (saveCaption's own guard).
     */
    const saveCaptionOnUnmount = useEffectEvent(() => {
        void saveCaption();
    });
    useEffect(() => {
        return () => {
            saveCaptionOnUnmount();
        };
    }, []);

    const takesBackground =
        Boolean(backgroundPhotoId) &&
        backgroundGoesWithSketch(
            media,
            mediaById.get(backgroundPhotoId),
            links
        );

    // Delete this item — and, for an annotated photo, the photo underneath it. A
    // sketch over a photo is ONE picture to whoever made it, and the photo is
    // hidden from the gallery while a sketch covers it, so deleting only the
    // sketch handed back an image they believed they had just thrown away.
    // Deleting the picture itself deletes it everywhere it is linked, too.
    async function doDelete() {
        const ids = takesBackground
            ? [media.id, backgroundPhotoId]
            : [media.id];
        for (const link of links.filter((one) => ids.includes(one.mediaId))) {
            await (await linkCollection?.findOne(link.id).exec())?.remove();
        }
        for (const id of ids) {
            if (collection) {
                const live = await collection.findOne(id).exec();
                await live?.remove();
            }
            await forgetMedia(id);
        }
        setConfirmDelete(false);
    }

    // Take a linked picture out of THIS description only — with its photo,
    // where it arrived as a sketch over one.
    async function doUnlink() {
        if (!linkHere) {
            return;
        }
        const going = links.filter(
            (link) =>
                link.ownerId === linkHere.ownerId &&
                (link.mediaId === media.id ||
                    link.mediaId === backgroundPhotoId)
        );
        for (const link of going) {
            await (await linkCollection?.findOne(link.id).exec())?.remove();
        }
        setConfirmDelete(false);
    }

    // Still loading: the same skeleton the card stood in as while its picture
    // was being prepared, so a fresh one does not flicker through a spinner.
    const preview = url ? (
        <CardMedia
            component="img"
            image={url}
            alt={caption || media.originalName}
            sx={{ height: CARD_MEDIA_HEIGHT, objectFit: 'cover' }}
        />
    ) : loading ? (
        <Skeleton
            variant="rectangular"
            animation="wave"
            height={CARD_MEDIA_HEIGHT}
        />
    ) : (
        <Box
            sx={{
                height: CARD_MEDIA_HEIGHT,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'action.hover'
            }}
        >
            <BrokenImageIcon color="disabled" />
        </Box>
    );

    return (
        <Card
            ref={cardRef}
            variant="outlined"
            sx={[
                { minWidth: 0 },
                focused && {
                    borderColor: 'primary.main',
                    boxShadow: (theme) =>
                        `0 0 0 1px ${theme.palette.primary.main}`
                }
            ]}
        >
            <Box sx={{ position: 'relative' }}>
                {onEdit ? (
                    <CardActionArea onClick={onEdit}>{preview}</CardActionArea>
                ) : (
                    preview
                )}
                {media.kind === 'sketch' && (
                    <GestureIcon
                        fontSize="small"
                        sx={{
                            position: 'absolute',
                            top: 4,
                            left: 4,
                            color: 'common.white',
                            filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.6))',
                            pointerEvents: 'none'
                        }}
                    />
                )}
                {linkHere && (
                    <Tooltip
                        title={<FormattedMessage id="media.link.linked" />}
                    >
                        <LinkIcon
                            fontSize="small"
                            aria-label={intl.formatMessage({
                                id: 'media.link.linked'
                            })}
                            sx={{
                                position: 'absolute',
                                bottom: 4,
                                left: 4,
                                color: 'common.white',
                                filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.6))'
                            }}
                        />
                    </Tooltip>
                )}
                {previewChosen && (
                    <Tooltip title={<FormattedMessage id="media.preview" />}>
                        <StarIcon
                            fontSize="small"
                            aria-label={intl.formatMessage({
                                id: 'media.preview'
                            })}
                            sx={{
                                position: 'absolute',
                                bottom: 4,
                                right: 4,
                                color: 'warning.light',
                                filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.6))'
                            }}
                        />
                    </Tooltip>
                )}
                {pending && (
                    <CloudQueueIcon
                        fontSize="small"
                        sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            color: 'common.white',
                            filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.6))',
                            pointerEvents: 'none'
                        }}
                    />
                )}
            </Box>
            <CardContent sx={{ p: 1.25, pb: 1, '&:last-child': { pb: 1 } }}>
                <TextField
                    fullWidth
                    size="small"
                    label={<FormattedMessage id="media.caption" />}
                    // The uploaded file's name doubles as the default name.
                    placeholder={media.originalName}
                    // Wraps instead of scrolling sideways, up to three lines; one
                    // line of text all the same — a break means nothing on the
                    // PDF, whose band runs the caption on.
                    multiline
                    // Empty, the field would grow to the wrapped file name it
                    // shows as the placeholder — a long camera name made photo
                    // cards taller than sketch cards. One line, cut short.
                    maxRows={caption ? 3 : 1}
                    sx={{
                        '& textarea::placeholder': {
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }
                    }}
                    value={caption}
                    disabled={!canEditMedia}
                    onChange={(event) =>
                        setCaption(event.target.value.replace(/\s*\n\s*/g, ' '))
                    }
                    onBlur={() => void saveCaption()}
                    // Enter still submits the record dialog around this card, as
                    // it did while this was a single-line input — a textarea
                    // would take it as a line break instead. The caption is
                    // written FIRST: without that the dialog closed with the
                    // text still only in the field.
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            event.preventDefault();
                            void saveCaption();
                            (event.target as HTMLElement)
                                .closest('form')
                                ?.requestSubmit();
                        }
                    }}
                />
                {otherPlaces.length > 0 && (
                    <Typography
                        variant="caption"
                        component="div"
                        sx={{ mt: 0.75, color: 'text.secondary' }}
                    >
                        <FormattedMessage id="media.link.alsoIn" />{' '}
                        {otherPlaces.map((place, index) => (
                            <span key={place.to}>
                                {index > 0 && '; '}
                                <Link
                                    component={RouterLink}
                                    to={place.to}
                                    onClick={linkClick(() =>
                                        onOpenPlace(place.to)
                                    )}
                                >
                                    {place.label}
                                </Link>
                            </span>
                        ))}
                    </Typography>
                )}
            </CardContent>
            {/* Rendered whether or not the user may edit: downloading is
                reading, and a read-only viewer still needs the file. */}
            <CardActions sx={{ pt: 0 }}>
                {canEdit && onEdit && (
                    <IconButton
                        size="small"
                        aria-label={intl.formatMessage({
                            id:
                                media.kind === 'photo'
                                    ? 'media.annotate'
                                    : 'media.editSketch'
                        })}
                        onClick={onEdit}
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>
                )}
                {canEdit && (
                    <HistoryIconButton onClick={() => setHistoryOpen(true)} />
                )}
                {onTogglePreview && (
                    <Tooltip
                        title={
                            <FormattedMessage
                                id={
                                    previewChosen
                                        ? 'media.preview.unset'
                                        : 'media.preview.set'
                                }
                            />
                        }
                    >
                        <span>
                            <IconButton
                                size="small"
                                disabled={busy || starring}
                                aria-pressed={previewChosen}
                                aria-label={intl.formatMessage({
                                    id: previewChosen
                                        ? 'media.preview.unset'
                                        : 'media.preview.set'
                                })}
                                onClick={() => void togglePreview()}
                            >
                                {previewChosen ? (
                                    <StarIcon
                                        fontSize="small"
                                        sx={{ color: 'warning.main' }}
                                    />
                                ) : (
                                    <StarBorderIcon fontSize="small" />
                                )}
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
                <Tooltip title={<FormattedMessage id="media.download" />}>
                    {/* A disabled button fires no events, so the tooltip needs
                        a wrapper to hang off. */}
                    <span>
                        <IconButton
                            size="small"
                            // A sketch draws itself from its scene, so it needs
                            // no cached image; a photo has nothing to save
                            // until its binary has resolved.
                            disabled={busy || (!url && !media.scene)}
                            aria-label={intl.formatMessage({
                                id: 'media.download'
                            })}
                            onClick={() => void download()}
                        >
                            <DownloadIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                {onRebake && (
                    <Tooltip title={<FormattedMessage id="media.rebake" />}>
                        <span>
                            <IconButton
                                size="small"
                                disabled={busy}
                                aria-label={intl.formatMessage({
                                    id: 'media.rebake'
                                })}
                                onClick={() => void rebake()}
                            >
                                <AutoFixHighIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
                <Tooltip
                    title={<FormattedMessage id="media.directLink.copy" />}
                >
                    <IconButton
                        size="small"
                        aria-label={intl.formatMessage({
                            id: 'media.directLink.copy'
                        })}
                        onClick={() => void copyDirectLink()}
                    >
                        <InsertLinkIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Box sx={{ flexGrow: 1 }} />
                {canUnlink && linkHere && (
                    <Tooltip title={<FormattedMessage id="media.unlink" />}>
                        <IconButton
                            size="small"
                            aria-label={intl.formatMessage({
                                id: 'media.unlink'
                            })}
                            onClick={() => setConfirmDelete(true)}
                        >
                            <LinkOffIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                {canEdit && detach && (
                    <Tooltip title={<FormattedMessage id="media.unlink" />}>
                        <IconButton
                            size="small"
                            aria-label={intl.formatMessage({
                                id: 'media.unlink'
                            })}
                            onClick={() => setConfirmDetach(true)}
                        >
                            <LinkOffIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                {/* Linked elsewhere, an original is only ever unlinked here:
                    deleting it would take it from every other place too. */}
                {canDelete && !linkHere && !detach && (
                    <IconButton
                        size="small"
                        aria-label={intl.formatMessage({ id: 'common.delete' })}
                        onClick={() => setConfirmDelete(true)}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                )}
            </CardActions>
            {detach && (
                <ConfirmDialog
                    open={confirmDetach}
                    title={<FormattedMessage id="media.unlink" />}
                    message={
                        <FormattedMessage
                            id="media.detach.confirm"
                            values={{ place: detach.heirLabel }}
                        />
                    }
                    confirmLabel={<FormattedMessage id="media.unlink" />}
                    onConfirm={async () => {
                        await detach.detach();
                        setConfirmDetach(false);
                    }}
                    onCancel={() => setConfirmDetach(false)}
                />
            )}
            {linkHere ? (
                <ConfirmDialog
                    open={confirmDelete}
                    title={<FormattedMessage id="media.unlink" />}
                    message={<FormattedMessage id="media.unlink.confirm" />}
                    confirmLabel={<FormattedMessage id="media.unlink" />}
                    onConfirm={doUnlink}
                    onCancel={() => setConfirmDelete(false)}
                />
            ) : (
                <ConfirmDialog
                    open={confirmDelete}
                    title={<FormattedMessage id="media.delete.title" />}
                    message={
                        <FormattedMessage
                            id={
                                takesBackground
                                    ? 'media.delete.confirmWithPhoto'
                                    : 'media.delete.confirm'
                            }
                        />
                    }
                    details={
                        otherPlaces.length > 0 ? (
                            <Alert severity="warning" sx={{ mt: 2 }}>
                                <FormattedMessage
                                    id="media.delete.shared"
                                    values={{
                                        places: otherPlaces
                                            .map((place) => place.label)
                                            .join('; ')
                                    }}
                                />
                            </Alert>
                        ) : undefined
                    }
                    confirmColor="error"
                    onConfirm={doDelete}
                    onCancel={() => setConfirmDelete(false)}
                />
            )}
            {historyOpen && (
                <HistoryDialog
                    apiBase="/api/media"
                    entityId={media.id}
                    open={historyOpen}
                    onClose={() => setHistoryOpen(false)}
                    primaryField="originalName"
                    subtitleField="kind"
                    labelPrefix="media"
                    title={intl.formatMessage({ id: 'media.history.title' })}
                    // Only the human-edited caption is text-diffable; the binary
                    // and the (large) sketch scene are not — those changes are
                    // NAMED under "weitere Änderungen" instead of diffed.
                    fields={[
                        {
                            key: 'caption',
                            label: intl.formatMessage({ id: 'media.caption' })
                        }
                    ]}
                />
            )}
        </Card>
    );
}
